import React from "react";
import { Link } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./PageContent.css";
import "./ContactPage.css";

export default function ContactPage() {
  // GET /api/contact → tbl_contents (page_id = 3), returns a single object
  // Shape: { id, title, detailsHtml, img }
  const { data: content, loading } = useApiData("contact");
  const { data: leaders } = useApiData("leadership");

  const principal = leaders?.find((l) =>
    String(l.role || "").toLowerCase().match(/principal|dean/)
  );
  const medicalSuperintendent = leaders?.find((l) =>
    String(l.role || "").toLowerCase().includes("medical superintendent")
  );

  const slugify = (str) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  // The CMS stores this as one blob of text where each line is meant to be
  // its own row (Dean's address, phone, fax, email... then a blank gap...
  // then the Superintendent's block). Sometimes that's raw "\n" characters,
  // sometimes <br>/<p> tags — so first normalize everything down to a flat
  // list of plain-text lines, then work with lines instead of HTML.
  const htmlToLines = (html) => {
    if (!html) return [];

    const normalized = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p\s*>/gi, "\n")
      .replace(/<\/div\s*>/gi, "\n");

    let text;
    if (typeof window !== "undefined" && window.DOMParser) {
      text = new DOMParser().parseFromString(normalized, "text/html").body
        .textContent || "";
    } else {
      text = normalized.replace(/<[^>]+>/g, "");
    }

    return text.split(/\n/).map((line) => line.trim());
  };

  // Given a run of lines, the first non-empty line is the section title
  // ("Dean and Principal," / "Superintendent,") and the rest — with any
  // leading/trailing blank lines trimmed — is the body.
  const splitSection = (lines) => {
    const start = lines.findIndex((l) => l.length > 0);
    if (start === -1) return { title: "", bodyLines: [] };

    const bodyLines = lines.slice(start + 1);
    while (bodyLines.length && bodyLines[0] === "") bodyLines.shift();
    while (bodyLines.length && bodyLines[bodyLines.length - 1] === "")
      bodyLines.pop();

    return { title: lines[start], bodyLines };
  };

  const escapeHtml = (str) =>
    str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Bold known field labels and turn emails/phone numbers into real links,
  // per line, so CMS text reads as a proper directory entry.
  const enhanceLine = (line) => {
    let out = escapeHtml(line);

    out = out.replace(
      /(Tel\.?\s*No\.?|Fax|E-?mail|Helpdesk|Emergency|Casualty)(\s*:?)/gi,
      (match) => `<strong>${match}</strong>`
    );

    out = out.replace(
      /([\w.+-]+@[\w-]+\.[\w.-]+)/g,
      (match) => `<a href="mailto:${match}">${match}</a>`
    );

    out = out.replace(
      /(\(?\d{3,4}\)?[\s-]?\d{6,8})/g,
      (match) => `<a href="tel:${match.replace(/[^\d+]/g, "")}">${match}</a>`
    );

    return out;
  };

  // Blank lines become a blank row (paragraph gap); real lines get enhanced
  // and end in a <br/> so each one renders on its own row.
  const renderBodyHtml = (bodyLines) =>
    bodyLines
      .map((line) => (line === "" ? "<br/>" : `${enhanceLine(line)}<br/>`))
      .join("");

  const allLines = htmlToLines(content?.detailsHtml);
  const superintendentStart = allLines.findIndex((l) =>
    /^superintendent\b/i.test(l)
  );

  const deanLines =
    superintendentStart === -1 ? allLines : allLines.slice(0, superintendentStart);
  const superLines =
    superintendentStart === -1 ? [] : allLines.slice(superintendentStart);

  const dean = splitSection(deanLines);
  const superintendent = splitSection(superLines);

  const deanBodyHtml = renderBodyHtml(dean.bodyLines);
  const superBodyHtml = renderBodyHtml(superintendent.bodyLines);

  const renderOfficialMessage = (leader, title) => {
    if (!leader) {
      return null;
    }

    return (
      <div className="official-message-block">
        <div className="official-message-title">{title}</div>

        <div className="official-message-body">
          <div className="official-message-top">
            <div className="official-photo-wrapper">
              <img
                src={leader.img}
                alt={leader.name}
                className="official-photo"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    leader.name || "Official"
                  )}&background=0b5d7a&color=fff&size=150`;
                }}
              />
            </div>

            <div className="official-message-text">
              <p>{leader.message}</p>
            </div>
          </div>

          <div className="official-name">{leader.name}</div>

          <div className="official-role">{leader.role}</div>

          <div className="official-more-wrapper">
            <Link
              to={`/leadership-message/${slugify(leader.role)}`}
              className="official-more"
            >
              More...
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className="content-page-wrapper">
      <div className="content-breadcrumb-wrapper">
        <div className="content-container">
          <div className="content-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i>
              Home
            </Link>

            <span className="breadcrumb-arrow">
              <i className="fa fa-angle-right"></i>
            </span>

            <span className="breadcrumb-current">Contact Us</span>
          </div>
        </div>
      </div>

      <div className="content-container">
        <div className="content-layout">
          <aside className="content-sidebar">
            <div className="official-info-card">
              <div className="official-info-header">
                <div className="official-info-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>
                <span>MESSAGE FROM</span>
              </div>

              <div className="official-info-body">
                {renderOfficialMessage(principal, "DEAN AND PRINCIPAL")}
                {renderOfficialMessage(
                  medicalSuperintendent,
                  "MEDICAL SUPERINTENDENT"
                )}
              </div>
            </div>
          </aside>

          <section className="content-main-card">
            {loading ? (
              <div className="content-loading">
                <div className="content-spinner"></div>
                <p>Loading contact details...</p>
              </div>
            ) : (
              <>
                <h1 className="notice-page-title">
                  {content?.title ? content.title.toUpperCase() : "CONTACT US"}
                </h1>

                <div className="contact-layout">
                  <div className="contact-card contact-card-dean">
                    <div className="contact-card-header">
                      <div className="contact-card-icon">
                        <i className="fa fa-user-md"></i>
                      </div>
                      <h2 className="contact-card-title">
                        {dean.title || "Dean and Principal"}
                      </h2>
                    </div>
                    <div
                      className="contact-card-body"
                      dangerouslySetInnerHTML={{ __html: deanBodyHtml }}
                    />
                  </div>

                  {superintendent.title || superintendent.bodyLines.length ? (
                    <div className="contact-card contact-card-super">
                      <div className="contact-card-header">
                        <div className="contact-card-icon">
                          <i className="fa fa-medkit"></i>
                        </div>
                        <h2 className="contact-card-title">
                          {superintendent.title || "Superintendent"}
                        </h2>
                      </div>
                      <div
                        className="contact-card-body"
                        dangerouslySetInnerHTML={{ __html: superBodyHtml }}
                      />
                    </div>
                  ) : null}

                  {content?.img ? (
                    <div className="contact-image-wrapper">
                      <img
                        src={content.img}
                        alt="Contact"
                        className="contact-image"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="contact-map-card">
                  <div className="contact-map-header">
                    <i className="fa fa-map-marker"></i>
                    <span>Location</span>
                  </div>

                  <div className="contact-map-wrap">
                    <iframe
                      className="contact-map-iframe"
                      title="map"
                      frameBorder="0"
                      scrolling="no"
                      src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d30122.464141061355!2d84.836553!3d19.312436!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x432ea01e2bf8ddb7!2sMaharaja+Krushna+Chandra+Gajapati+Medical+College+and+Hospital!5e0!3m2!1sen!2sin!4v1454566620396"
                    />
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
